import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n/i18n';
import { SoundProvider } from './contexts/SoundContext';
import { BookingProvider } from './contexts/BookingContext';
import BookingModal from './components/UI/BookingModal';
import Luna from './components/UI/Luna';
import './App.css';

// Layout Components
import Navigation from './components/Layout/Navigation';
import Footer from './components/Layout/Footer';
import LoadingSpinner from './components/UI/LoadingSpinner';
import ScrollToTop from './components/UI/ScrollToTop';
import LanguageRoute from './components/LanguageRoute';
import HreflangTags from './components/SEO/HreflangTags';
import StructuredData from './components/SEO/StructuredData';

// Page Components (Lazy loaded for performance)
const Homepage = React.lazy(() => import('./pages/Homepage'));
const TreatmentsPage = React.lazy(() => import('./pages/TreatmentsPage'));
const TreatmentDetailPage = React.lazy(() => import('./pages/TreatmentDetailPage'));
const WellnessQuiz = React.lazy(() => import('./pages/WellnessQuiz'));

const VirtualEscape = React.lazy(() => import('./pages/VirtualEscape'));
const Blog = React.lazy(() => import('./pages/Blog'));
const BlogPost = React.lazy(() => import('./pages/BlogPost'));
const BookingPage = React.lazy(() => import('./pages/BookingPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));

// Page transition variants
const pageVariants = {
  initial: { 
    opacity: 0, 
    y: 20,
    scale: 0.98
  },
  animate: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1] // Sacred easing
    }
  },
  exit: { 
    opacity: 0, 
    y: -20,
    scale: 1.02,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  }
};

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <SoundProvider>
        <BookingProvider>
          <Router>
          <ScrollToTop />
          <HreflangTags />
          <StructuredData />
          <div className="App">
          <Navigation />
          
          <main className="main-content">
            <AnimatePresence mode="wait">
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route 
                    path="/" 
                    element={
                      <motion.div
                        variants={pageVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                      >
                        <Homepage />
                      </motion.div>
                    } 
                  />
                  
                  <Route 
                    path="/treatments" 
                    element={
                      <motion.div
                        variants={pageVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                      >
                        <TreatmentsPage />
                      </motion.div>
                    } 
                  />
                  
                  <Route 
                    path="/treatments/:treatmentId" 
                    element={
                      <motion.div
                        variants={pageVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                      >
                        <TreatmentDetailPage />
                      </motion.div>
                    } 
                  />
                  
                  <Route 
                    path="/wellness-quiz" 
                    element={
                      <motion.div
                        variants={pageVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                      >
                        <WellnessQuiz />
                      </motion.div>
                    } 
                  />
                  

                  <Route 
                    path="/journal" 
                    element={
                      <motion.div
                        variants={pageVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                      >
                        <Blog />
                      </motion.div>
                    } 
                  />
                  
                  <Route 
                    path="/journal/:postSlug" 
                    element={
                      <motion.div
                        variants={pageVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                      >
                        <BlogPost />
                      </motion.div>
                    } 
                  />
                  
                  <Route 
                    path="/booking" 
                    element={
                      <motion.div
                        variants={pageVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                      >
                        <BookingPage />
                      </motion.div>
                    } 
                  />
                  

                  
                  <Route 
                    path="/contact" 
                    element={
                      <motion.div
                        variants={pageVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                      >
                        <ContactPage />
                      </motion.div>
                    } 
                  />
                  
                  <Route 
                    path="/privacy" 
                    element={
                      <motion.div
                        variants={pageVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                      >
                        <PrivacyPolicy />
                      </motion.div>
                    } 
                  />
                  
                  <Route 
                    path="/terms" 
                    element={
                      <motion.div
                        variants={pageVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                      >
                        <TermsOfService />
                      </motion.div>
                    } 
                  />
                  
                  {/* Language-specific route aliases */}
                  <Route 
                    path="/:lang" 
                    element={
                      <LanguageRoute>
                        <motion.div
                          variants={pageVariants}
                          initial="initial"
                          animate="animate"
                          exit="exit"
                        >
                          <Homepage />
                        </motion.div>
                      </LanguageRoute>
                    } 
                  />
                  
                  <Route 
                    path="/:lang/treatments" 
                    element={
                      <LanguageRoute>
                        <motion.div
                          variants={pageVariants}
                          initial="initial"
                          animate="animate"
                          exit="exit"
                        >
                          <TreatmentsPage />
                        </motion.div>
                      </LanguageRoute>
                    } 
                  />
                  
                  <Route 
                    path="/:lang/treatments/:treatmentId" 
                    element={
                      <LanguageRoute>
                        <motion.div
                          variants={pageVariants}
                          initial="initial"
                          animate="animate"
                          exit="exit"
                        >
                          <TreatmentDetailPage />
                        </motion.div>
                      </LanguageRoute>
                    } 
                  />
                  
                  <Route 
                    path="/:lang/wellness-quiz" 
                    element={
                      <LanguageRoute>
                        <motion.div
                          variants={pageVariants}
                          initial="initial"
                          animate="animate"
                          exit="exit"
                        >
                          <WellnessQuiz />
                        </motion.div>
                      </LanguageRoute>
                    } 
                  />
                  
                  <Route 
                    path="/:lang/journal" 
                    element={
                      <LanguageRoute>
                        <motion.div
                          variants={pageVariants}
                          initial="initial"
                          animate="animate"
                          exit="exit"
                        >
                          <Blog />
                        </motion.div>
                      </LanguageRoute>
                    } 
                  />
                  
                  <Route 
                    path="/:lang/journal/:postSlug" 
                    element={
                      <LanguageRoute>
                        <motion.div
                          variants={pageVariants}
                          initial="initial"
                          animate="animate"
                          exit="exit"
                        >
                          <BlogPost />
                        </motion.div>
                      </LanguageRoute>
                    } 
                  />
                  
                  <Route 
                    path="/:lang/booking" 
                    element={
                      <LanguageRoute>
                        <motion.div
                          variants={pageVariants}
                          initial="initial"
                          animate="animate"
                          exit="exit"
                        >
                          <BookingPage />
                        </motion.div>
                      </LanguageRoute>
                    } 
                  />
                  
                  <Route 
                    path="/:lang/contact" 
                    element={
                      <LanguageRoute>
                        <motion.div
                          variants={pageVariants}
                          initial="initial"
                          animate="animate"
                          exit="exit"
                        >
                          <ContactPage />
                        </motion.div>
                      </LanguageRoute>
                    } 
                  />
                  
                  <Route 
                    path="/:lang/privacy" 
                    element={
                      <LanguageRoute>
                        <motion.div
                          variants={pageVariants}
                          initial="initial"
                          animate="animate"
                          exit="exit"
                        >
                          <PrivacyPolicy />
                        </motion.div>
                      </LanguageRoute>
                    } 
                  />
                  
                  <Route 
                    path="/:lang/terms" 
                    element={
                      <LanguageRoute>
                        <motion.div
                          variants={pageVariants}
                          initial="initial"
                          animate="animate"
                          exit="exit"
                        >
                          <TermsOfService />
                        </motion.div>
                      </LanguageRoute>
                    } 
                  />
                  
                  <Route 
                    path="/:lang/virtual-escape" 
                    element={
                      <LanguageRoute>
                        <motion.div
                          variants={pageVariants}
                          initial="initial"
                          animate="animate"
                          exit="exit"
                        >
                          <VirtualEscape />
                        </motion.div>
                      </LanguageRoute>
                    } 
                  />
                </Routes>
              </Suspense>
            </AnimatePresence>
          </main>
          
          <Footer />
          <BookingModal />
          <Luna />
        </div>
      </Router>
        </BookingProvider>
      </SoundProvider>
    </I18nextProvider>
  );
}

export default App;
